import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Permissions } from '@app/core/guard/Permissions';
import { DashboardPreferenceBlock } from '@interfaces/dashboard-preferences/responses/dashboard-preferences.response';
import { PermissionsService } from '@services/helpers/permissions.service';
import { DashboardPreferencesHttpService } from '@services/http/dashboard-preferences/dashboard-preferences-http.service';
import { Observable, filter, finalize, map, of, take, tap } from 'rxjs';
import { HomeSelectionHints } from '@features/dashboard/dashboard-management/utils/home-block-availability';
import {
  DEFAULT_HOME_BLOCK_IDS,
  HomeBlockId,
  HomeLayoutItem,
} from '@features/dashboard/dashboard-management/utils/home-blocks';
import {
  mergeWithUnavailableBlocks,
  resolveHomeLayout,
} from '@features/dashboard/dashboard-management/utils/resolve-home-layout';

/** Chave da arrumação da Home no navegador (quem não tem `DASHBOARDPREFERENCES_MANAGE`, ou se a leitura falhar). */
export const HOME_LAYOUT_STORAGE_KEY = 'cognito.home.layout.v1';

/** Falha técnica ao salvar (rede, 500): o erro de negócio do backend aparece com a mensagem dele. */
export const HOME_LAYOUT_SAVE_ERROR =
  'Não foi possível salvar a sua Home agora. A arrumação continua aberta: tente Salvar de novo em alguns minutos.';

/** Onde a arrumação fica: na conta (com a permissão) ou neste navegador. */
export type HomeLayoutSource = 'account' | 'browser';

/**
 * Arrumação da Home (card 3788): ordem dos blocos e quais estão ligados. Provido pela página da Home; a gaveta
 * "Personalizar Dashboard" usa a mesma instância.
 *
 * <remarks>
 * - **Com `DASHBOARDPREFERENCES_MANAGE`** (`source = 'account'`): lê `GET dashboard/preferences` uma vez e grava a
 *   arrumação inteira (`PUT`) ao Salvar. Vale em qualquer computador. Se a leitura falhar, a tela usa o que estiver no
 *   navegador (ou o padrão) e o Salvar continua indo para a conta.
 * - **Sem a permissão** (`source = 'browser'`): lê e grava só no `localStorage` (`HOME_LAYOUT_STORAGE_KEY`), sem chamar
 *   o backend. Sem armazenamento (aba anônima, bloqueio), vale só nesta visita.
 * - A página informa quais blocos estão disponíveis (`setAvailable`: flag e permissões); bloco indisponível some da
 *   gaveta, mas a arrumação gravada dele é preservada ao salvar (`mergeWithUnavailableBlocks`).
 * </remarks>
 */
@Injectable()
export class HomeLayoutService {
  private readonly http = inject(DashboardPreferencesHttpService);
  private readonly permissions = inject(PermissionsService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private readonly savedSignal = signal<DashboardPreferenceBlock[]>([]);
  private readonly availableSignal = signal<HomeBlockId[]>([...DEFAULT_HOME_BLOCK_IDS]);
  private readonly canSaveToAccountSignal = signal(false);
  private readonly loadedSignal = signal(false);
  private readonly savingSignal = signal(false);
  private readonly selectionHintsSignal = signal<HomeSelectionHints>({});

  /** Pode gravar na conta (`DASHBOARDPREFERENCES_MANAGE`). */
  readonly canSaveToAccount = this.canSaveToAccountSignal.asReadonly();
  /** `true` depois da primeira leitura (da conta ou do navegador), deu certo ou não. */
  readonly loaded = this.loadedSignal.asReadonly();
  /** Dicas da gaveta para blocos que a escolha de empresa de agora não mostra (`homeSelectionHints`). */
  readonly selectionHints = this.selectionHintsSignal.asReadonly();
  /** `true` enquanto o Salvar está indo para a conta. */
  readonly saving = this.savingSignal.asReadonly();
  /** Onde a arrumação fica. */
  readonly source = computed<HomeLayoutSource>(() =>
    this.canSaveToAccountSignal() ? 'account' : 'browser'
  );
  /** Arrumação que vale agora: só blocos disponíveis, na ordem gravada, com os novos no fim. */
  readonly layout = computed(() => resolveHomeLayout(this.savedSignal(), this.availableSignal()));
  /** Ids dos blocos ligados, na ordem. */
  readonly visibleIds = computed(() =>
    this.layout()
      .filter(item => item.visible)
      .map(item => item.id)
  );
  /** A pessoa desligou todos os blocos disponíveis (a página mostra o estado vazio). */
  readonly allHidden = computed(() => this.layout().length > 0 && this.visibleIds().length === 0);

  /** Lê a arrumação: da conta, com a permissão; do navegador, sem ela. Chamado uma vez pela página. */
  init(): void {
    this.permissions
      .hasPermission(Permissions.DashboardPreferences.MANAGE)
      .pipe(
        filter(value => value !== undefined),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(canManage => {
        this.canSaveToAccountSignal.set(!!canManage);
        if (canManage) {
          this.loadFromAccount();
          return;
        }
        this.savedSignal.set(normalizeBlocks(this.readStorage()));
        this.loadedSignal.set(true);
      });
  }

  /**
   * Blocos disponíveis para a pessoa agora (flag e permissões), na ordem padrão. Chamado pela página quando muda.
   * @param ids Ids disponíveis.
   */
  setAvailable(ids: readonly HomeBlockId[]): void {
    const next = DEFAULT_HOME_BLOCK_IDS.filter(id => ids.includes(id));
    if (next.join('|') !== this.availableSignal().join('|')) {
      this.availableSignal.set(next);
    }
  }

  /**
   * Dicas de seleção de empresa para a gaveta. Chamado pela página quando a empresa, a flag ou as permissões mudam.
   * @param hints Dica por bloco.
   */
  setSelectionHints(hints: HomeSelectionHints): void {
    const current = this.selectionHintsSignal();
    const same =
      Object.keys(hints).length === Object.keys(current).length &&
      Object.entries(hints).every(([id, text]) => current[id as HomeBlockId] === text);
    if (!same) {
      this.selectionHintsSignal.set(hints);
    }
  }

  /** Arrumação padrão (ordem do protótipo, tudo ligado) com os blocos disponíveis: o "Restaurar Padrão" da gaveta. */
  defaultLayout(): HomeLayoutItem[] {
    return resolveHomeLayout([], this.availableSignal());
  }

  /**
   * Grava a arrumação da gaveta. Na conta, devolve depois do `PUT` (o erro vai para quem chamou, que mostra a mensagem
   * do backend); no navegador, grava e devolve na hora. A Home só muda depois de gravar.
   * @param items Blocos da gaveta, na ordem nova, com `visible`.
   */
  save(items: readonly HomeLayoutItem[]): Observable<void> {
    const blocks = mergeWithUnavailableBlocks(items, this.savedSignal());
    if (!this.canSaveToAccountSignal()) {
      this.writeStorage(blocks);
      this.savedSignal.set(blocks);
      return of(undefined);
    }
    this.savingSignal.set(true);
    return this.http.save({ blocks }).pipe(
      map(response => (response?.blocks ? normalizeBlocks(response.blocks) : blocks)),
      tap(saved => this.savedSignal.set(saved)),
      map(() => undefined),
      finalize(() => this.savingSignal.set(false))
    );
  }

  /** Lê da conta; falhou, usa o navegador (ou o padrão) para a Home não ficar travada. */
  private loadFromAccount(): void {
    this.http
      .get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.savedSignal.set(normalizeBlocks(response?.blocks));
          this.loadedSignal.set(true);
        },
        error: () => {
          this.savedSignal.set(normalizeBlocks(this.readStorage()));
          this.loadedSignal.set(true);
        },
      });
  }

  /** Lê a arrumação do `localStorage`, protegido: fora do navegador, sem armazenamento ou com texto quebrado, `null`. */
  private readStorage(): unknown {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    try {
      const text = window.localStorage.getItem(HOME_LAYOUT_STORAGE_KEY);
      return text ? JSON.parse(text) : null;
    } catch {
      return null;
    }
  }

  /**
   * Grava a arrumação no `localStorage`, protegido (sem armazenamento, vale só nesta visita).
   * @param blocks Arrumação.
   */
  private writeStorage(blocks: DashboardPreferenceBlock[]): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      window.localStorage.setItem(HOME_LAYOUT_STORAGE_KEY, JSON.stringify({ blocks }));
    } catch {
      // Sem armazenamento: vale só nesta visita.
    }
  }
}

/**
 * Aceita `{ blocks: [...] }` ou a lista direto e devolve só itens com `id` texto (o `visible` ausente vale `true`).
 * Ids desconhecidos ficam: quem filtra é o `resolveHomeLayout`.
 * @param value Lido do servidor ou do navegador.
 */
function normalizeBlocks(value: unknown): DashboardPreferenceBlock[] {
  const list = Array.isArray(value)
    ? value
    : Array.isArray((value as { blocks?: unknown } | null)?.blocks)
      ? (value as { blocks: unknown[] }).blocks
      : [];
  return list
    .filter(
      (item): item is { id: string; visible?: unknown } =>
        !!item && typeof (item as { id?: unknown }).id === 'string'
    )
    .map(item => ({ id: item.id, visible: item.visible !== false }));
}
